import React, { useState } from 'react';
import { Typography, Breadcrumb, Card, Row, Col, Button, Tabs, Table, Tag, Space, Statistic, Progress, Calendar, Badge, Select, InputNumber, Form, message } from 'antd';
import { 
  DollarOutlined, 
  CalendarOutlined, 
  PlusOutlined, 
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;

const BudgetsScheduling = () => {
  const [activeTab, setActiveTab] = useState('planner');

  // Budget data
  const [budgets] = useState([
    { id: 1, campaign: 'Summer Sale Campaign', totalBudget: 5000, spent: 3200, remaining: 1800, status: 'active', period: 'Monthly' },
    { id: 2, campaign: 'Brand Awareness', totalBudget: 10000, spent: 8500, remaining: 1500, status: 'active', period: 'Quarterly' },
    { id: 3, campaign: 'Product Launch', totalBudget: 15000, spent: 15000, remaining: 0, status: 'completed', period: 'One-time' },
    { id: 4, campaign: 'Holiday Special', totalBudget: 8000, spent: 2400, remaining: 5600, status: 'scheduled', period: 'Monthly' },
  ]);

  // Scheduled campaigns
  const [scheduledCampaigns] = useState([
    { date: '2024-12-05', campaigns: [{ name: 'Morning Flash Sale', time: '09:00 AM', status: 'scheduled' }] },
    { date: '2024-12-08', campaigns: [{ name: 'Weekend Promo', time: '10:00 AM', status: 'scheduled' }] },
    { date: '2024-12-10', campaigns: [{ name: 'Mid-Week Special', time: '02:00 PM', status: 'scheduled' }] },
    { date: '2024-12-15', campaigns: [{ name: 'Holiday Campaign', time: '08:00 AM', status: 'scheduled' }, { name: 'Evening Sale', time: '06:00 PM', status: 'scheduled' }] },
    { date: '2024-12-20', campaigns: [{ name: 'Year-End Clearance', time: '12:00 PM', status: 'scheduled' }] },
  ]);

  // Budget columns
  const budgetColumns = [
    { 
      title: 'Campaign', 
      dataIndex: 'campaign', 
      key: 'campaign',
      render: (text) => <Text strong>{text}</Text>
    },
    { 
      title: 'Total Budget', 
      dataIndex: 'totalBudget', 
      key: 'totalBudget',
      render: (amount) => `$${amount.toLocaleString()}`
    },
    { 
      title: 'Spent', 
      dataIndex: 'spent', 
      key: 'spent',
      render: (amount) => <Text type="danger">${amount.toLocaleString()}</Text>
    },
    { 
      title: 'Remaining', 
      dataIndex: 'remaining', 
      key: 'remaining',
      render: (amount) => <Text type="success">${amount.toLocaleString()}</Text>
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (_, record) => {
        const percentage = (record.spent / record.totalBudget) * 100;
        return (
          <Progress 
            percent={Math.round(percentage)} 
            status={percentage >= 100 ? 'exception' : percentage >= 80 ? 'normal' : 'success'}
            style={{ width: 120 }}
          />
        );
      }
    },
    { 
      title: 'Period', 
      dataIndex: 'period', 
      key: 'period',
      render: (text) => <Tag>{text}</Tag>
    },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status',
      render: (status) => {
        const colors = { active: 'green', completed: 'blue', scheduled: 'orange' };
        return <Tag color={colors[status]}>{status}</Tag>;
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      render: () => (
        <Space>
          <Button size="small" icon={<EditOutlined />}>Edit</Button>
          <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
        </Space>
      ),
    },
  ];

  // Calendar cell render
  const dateCellRender = (value) => {
    const dateStr = value.format('YYYY-MM-DD');
    const daySchedule = scheduledCampaigns.find(s => s.date === dateStr);
    
    if (daySchedule) {
      return (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {daySchedule.campaigns.map((campaign, index) => (
            <li key={index}>
              <Badge status="processing" text={
                <Text style={{ fontSize: 11 }}>{campaign.name}</Text>
              } />
            </li>
          ))}
        </ul>
      );
    }
    return null;
  };

  const onDateSelect = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    const daySchedule = scheduledCampaigns.find(s => s.date === dateStr);
    if (daySchedule) {
      message.info(`${daySchedule.campaigns.length} campaign(s) scheduled for ${date.format('MMMM D, YYYY')}`);
    }
  };

  const tabItems = [
    {
      key: 'planner',
      label: (
        <span>
          <DollarOutlined /> Budget Planner
        </span>
      ),
      children: (
        <div>
          {/* Summary Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Budget"
                  value={38000}
                  prefix="$"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Spent"
                  value={29100}
                  prefix="$"
                  valueStyle={{ color: '#cf1322' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Remaining"
                  value={8900}
                  prefix="$"
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Budget Utilization"
                  value={76.6}
                  suffix="%"
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Create Budget Button */}
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Title level={5} style={{ margin: 0 }}>Campaign Budgets</Title>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  Manage and track your campaign budgets
                </Paragraph>
              </Col>
              <Col>
                <Button icon={<PlusOutlined />} type="primary" size="large">
                  Create Budget
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Budget Allocation Form */}
          <Card title="Quick Budget Allocation" style={{ marginBottom: 24 }}>
            <Form layout="vertical">
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item label="Campaign Name" name="campaignName">
                    <Select size="large" placeholder="Select campaign">
                      <Select.Option value="campaign1">Summer Sale</Select.Option>
                      <Select.Option value="campaign2">Brand Awareness</Select.Option>
                      <Select.Option value="campaign3">Product Launch</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Budget Amount" name="amount">
                    <InputNumber
                      size="large"
                      style={{ width: '100%' }}
                      prefix="$"
                      min={0}
                      placeholder="Enter amount"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Period" name="period">
                    <Select size="large" placeholder="Select period">
                      <Select.Option value="daily">Daily</Select.Option>
                      <Select.Option value="weekly">Weekly</Select.Option>
                      <Select.Option value="monthly">Monthly</Select.Option>
                      <Select.Option value="quarterly">Quarterly</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item>
                <Button type="primary" size="large">
                  Allocate Budget
                </Button>
              </Form.Item>
            </Form>
          </Card>

          {/* Budgets Table */}
          <Card title="Active Budgets">
            <Table
              columns={budgetColumns}
              dataSource={budgets}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </div>
      )
    },
    {
      key: 'calendar',
      label: (
        <span>
          <CalendarOutlined /> Scheduling Calendar
        </span>
      ),
      children: (
        <div>
          {/* Schedule Summary */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Scheduled Campaigns"
                  value={7}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Active Today"
                  value={2}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="This Month"
                  value={15}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Schedule Button */}
          <Card style={{ marginBottom: 24 }}>
            <Row gutter={16} align="middle">
              <Col flex="auto">
                <Title level={5} style={{ margin: 0 }}>Campaign Schedule</Title>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  View and manage your scheduled campaigns
                </Paragraph>
              </Col>
              <Col>
                <Button icon={<PlusOutlined />} type="primary" size="large">
                  Schedule Campaign
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Calendar */}
          <Card>
            <Calendar 
              dateCellRender={dateCellRender}
              onSelect={onDateSelect}
            />
          </Card>

          {/* Upcoming Campaigns */}
          <Card title="Upcoming Campaigns" style={{ marginTop: 24 }}>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {scheduledCampaigns.map((schedule, index) => (
                <Card key={index} type="inner" size="small">
                  <Row align="middle" gutter={16}>
                    <Col flex="auto">
                      <Space direction="vertical" size="small">
                        <Text strong>{schedule.date}</Text>
                        {schedule.campaigns.map((campaign, idx) => (
                          <div key={idx}>
                            <Badge status="processing" />
                            <Text>{campaign.name}</Text>
                            <Text type="secondary" style={{ marginLeft: 8 }}>
                              {campaign.time}
                            </Text>
                          </div>
                        ))}
                      </Space>
                    </Col>
                    <Col>
                      <Space>
                        <Button size="small" icon={<EditOutlined />}>Edit</Button>
                        <Button size="small" danger icon={<DeleteOutlined />}>Cancel</Button>
                      </Space>
                    </Col>
                  </Row>
                </Card>
              ))}
            </Space>
          </Card>
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Budgets & Scheduling' }]} />
      
      <div style={{ marginTop: 12, marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>Budgets & Scheduling</Title>
        <Paragraph type="secondary" style={{ marginTop: 8 }}>
          Plan your campaign budgets and schedule your advertising activities
        </Paragraph>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
        type="card"
      />
    </div>
  );
};

export default BudgetsScheduling;
